import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Target,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ShoppingBag,
  Flame,
  Snowflake,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import {
  cn,
  formatBRL,
  formatPercent,
  formatRelative,
  stageColor,
  stageLabel,
  temperatureColor,
  temperatureLabel,
} from '../lib/utils.js';

function roasBadge(roas: number) {
  if (roas >= 2)
    return { label: 'Vencedor', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', icon: Flame };
  if (roas >= 1)
    return { label: 'Breakeven', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: TrendingUp };
  if (roas > 0)
    return { label: 'Negativo', color: 'bg-rose-500/15 text-rose-300 border-rose-500/30', icon: TrendingDown };
  return { label: 'Sem venda', color: 'bg-slate-500/15 text-slate-400 border-slate-500/30', icon: Snowflake };
}

export function CampanhaDetail(): JSX.Element {
  const { campaign } = useParams<{ campaign: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['campaign-detail', campaign],
    queryFn: () => apiClient.campaignDetail(campaign!),
    enabled: !!campaign,
  });

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Carregando campanha...</div>;
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Link
          to="/trafego"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para Tráfego
        </Link>
        <div className="text-rose-400">Campanha não encontrada.</div>
      </div>
    );
  }

  const { summary, creatives, recentContacts } = data;
  const overallBadge = roasBadge(summary.roas);
  const OverallIcon = overallBadge.icon;
  const maxCreativeSpend = creatives.length
    ? Math.max(...creatives.map((c) => Number(c.spendCents)))
    : 0;

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/trafego"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Tráfego
        </Link>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono text-xs">{data.campaign}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Target className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">
              Campanha {data.campaign}
            </h1>
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border',
                overallBadge.color,
              )}
            >
              <OverallIcon className="h-3 w-3" />
              ROAS {summary.roas.toFixed(2)}x · {overallBadge.label}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            {summary.creatives} criativos · {summary.events} eventos de spend ·{' '}
            {data.month}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
              <DollarSign className="h-3 w-3" /> Spend
            </div>
            <div className="text-lg font-bold tabular-nums">
              {formatBRL(summary.spendCents)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
              <ShoppingBag className="h-3 w-3" /> Receita
            </div>
            <div className="text-lg font-bold tabular-nums">
              {formatBRL(summary.grossCents)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
              <Users className="h-3 w-3" /> Contatos
            </div>
            <div className="text-lg font-bold tabular-nums">{summary.contacts}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
              <ShoppingBag className="h-3 w-3" /> Pedidos
            </div>
            <div className="text-lg font-bold tabular-nums">{summary.orders}</div>
            <div className="text-[10px] text-muted-foreground">
              CR: {formatPercent(summary.conversionRate)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
              <Users className="h-3 w-3" /> CPA
            </div>
            <div className="text-lg font-bold tabular-nums">
              {formatBRL(summary.cpaCents)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Criativos */}
        <Card>
          <CardHeader>
            <CardTitle>Criativos da campanha</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/30">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Criativo</th>
                  <th className="px-3 py-2 font-medium text-right">Spend</th>
                  <th className="px-3 py-2 font-medium text-right">Pedidos</th>
                  <th className="px-3 py-2 font-medium text-right">ROAS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {creatives.map((c) => {
                  const spend = Number(c.spendCents);
                  const gross = Number(c.grossCents);
                  const roas = spend > 0 ? gross / spend : 0;
                  const badge = roasBadge(roas);
                  const Icon = badge.icon;
                  const pct = maxCreativeSpend ? (spend / maxCreativeSpend) * 100 : 0;
                  return (
                    <tr key={c.creative} className="hover:bg-muted/30">
                      <td className="px-3 py-2.5">
                        <div className="font-mono text-sm font-medium">{c.creative}</div>
                        <div className="h-1 rounded-full bg-muted/40 overflow-hidden mt-1 max-w-[120px]">
                          <div
                            className="h-full bg-rose-500/60"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {formatBRL(spend)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {c.orders}
                        <div className="text-[10px] text-muted-foreground">
                          {formatBRL(gross)}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border',
                            badge.color,
                          )}
                        >
                          <Icon className="h-3 w-3" />
                          {roas.toFixed(2)}x
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {creatives.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">
                      Nenhum criativo encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Contatos recentes */}
        <Card>
          <CardHeader>
            <CardTitle>Contatos da campanha ({recentContacts.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto divide-y divide-border/40">
              {recentContacts.map((c) => (
                <Link
                  key={c.id}
                  to={`/contatos/${c.id}`}
                  className="block px-4 py-3 hover:bg-muted/30"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium truncate">
                          {c.displayName ?? '—'}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn('text-[10px]', temperatureColor[c.temperature])}
                        >
                          {temperatureLabel[c.temperature] ?? c.temperature}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.emailDisplay}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-1">
                        {(c.geoCity || c.geoState) && (
                          <span>{[c.geoCity, c.geoState].filter(Boolean).join(' / ')}</span>
                        )}
                        <Badge
                          variant="outline"
                          className={cn('text-[10px]', stageColor[c.lifecycleStage])}
                        >
                          {stageLabel[c.lifecycleStage] ?? c.lifecycleStage}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {c.ordersCount > 0 ? (
                        <>
                          <div className="text-sm font-medium text-emerald-400 tabular-nums">
                            {formatBRL(c.ltvCents)}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {c.ordersCount} pedido{c.ordersCount === 1 ? '' : 's'}
                          </div>
                        </>
                      ) : (
                        <div className="text-[10px] text-muted-foreground">sem compra</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {formatRelative(c.createdAt)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              {recentContacts.length === 0 && (
                <div className="px-4 py-6 text-sm text-muted-foreground text-center">
                  Nenhum contato com UTM dessa campanha.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
